import React, { useEffect, useState, memo, Fragment, Suspense } from 'react'
import { dollarUSLocale } from '../../helpers/dollarUSLocale'
import { formatTransit, getTransitInDays } from '../../helpers/transitTime'
import { KTSVG } from '../../../_metronic/helpers'
import { CircularProgress, LinearProgress, MenuItem, Paper, Rating, TextField, Tooltip, Box} from '@mui/material'
import MaterialTable, { Column } from '@material-table/core'
import { RowTableObject } from '../../pages/get-quote/models'
import { tableStyles } from '../TableStyles'
import QuoteBreakdownModal from '../LTLandVolumeTLPages/Modals/QuoteBreakdownModal'
import axios from 'axios'
import { checkErrorAPICall } from '../../helpers/checkErrorAPICall'
import { MTableBodyRowStyled } from '../QuotesGridPage/Table/Table.styled'
import { stylesMUIInput } from '../stylesMUIInput'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import clsx from 'clsx'
import { useStyles } from '../LTLandVolumeTLPages/FreightInfo/FreightInformation/FreightInformationStyles'
import ClearIcon from '@mui/icons-material/Clear';
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { UserModel } from '../../modules/auth/models/UserModel'
import { RootState } from '../../../setup'

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils'


interface Data {
    id: number;
    service: string;
    total: number;
    transitDays: string;
    name: string;    
  }
  
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (String(orderBy) === 'transitDays') {
      return getTransitInDays(b) - getTransitInDays(a)
    }
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
  
  type Order = 'asc' | 'desc';
  
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }
  
  interface HeadCell {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
  }
  
  const headCells: readonly HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: true,
      label: 'Carrier',
    },
    {
      id: 'service',
      numeric: false,
      disablePadding: false,
      label: 'Service Level',
    },
    {
      id: 'transitDays',
      numeric: false,
      disablePadding: false,
      label: 'Est. Transit',
    },
    {
      id: 'total',
      numeric: true,
      disablePadding: false,
      label: 'Total',
    },
  ];
  
  interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
    // onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
  }
  
  function EnhancedTableHead(props: EnhancedTableProps) {
    const { numSelected, order, orderBy, onRequestSort } =
      props;
    const createSortHandler =
      (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
      };
  
    return (
      <TableHead>
        <TableRow>
  
          {headCells.map((headCell) => (
          <Fragment key={headCell.id}>
            <TableCell            
              align={headCell.label === 'Total' ? 'right' : headCell.label === 'Est. Transit' ? 'center' : 'left'}
              sx={{width: headCell.label === 'Service Level' ? '50%' : 'calc(16.6667% + 0px)'}}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
              className={headCell.label === 'Est. Transit' ? 'd-flex w-100' : undefined}
            >            
              {headCell.label === 'Est. Transit' ? <div style={{width: 30}}>&nbsp;</div> : null}
            
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
            </Fragment>
          ))}
        </TableRow>
      </TableHead>
    );
  }
  

const RatesTable = memo(({rates, setFormikValues, handleClose, linearProgress, deleteToolbar, ratesStopped, setRatesStopped, source, setVendorName, vendorsFiltered, vendorsProcessed, formikValues, setOpenAddRateRemarksModal}) => {
    const fontSize = useSelector<RootState>(({pages}) => pages.fontSize) as number
    const classesInput = useStyles({props: {}})
    const classes = tableStyles()
    const dispatch = useDispatch()
    const [quoteBreakdownModalData, setQuoteBreakdownModalData] = useState({})
    const [openQuoteBreakdownModal, setOpenQuoteBreakdownModal] = useState(false)
    const [loadingSaveRate, setLoadingSaveRate] = useState(false)
    const [filteredRates, setFilteredRates] = useState([])
    const [rateTypeSelected, setRateTypeSelected] = useState('ALL RATES')
    const [rateTypes, setRateTypes] = useState([])
    const [buttonColor, setButtonColor] = useState('btn-danger')
    const [selectedId, setSelectedId] = useState(false)
    const user: UserModel = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel

    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('total');
    const [selected, setSelected] = React.useState<readonly number[]>([]);

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const visibleRows = 
    React.useMemo(
        () =>
            [...filteredRates]
            .sort(getComparator(order, orderBy)),
            // .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [order, orderBy, rates, filteredRates],
    );  


    const selectRate = (e, rowData) => {
        // switch (e.detail) {
        //     case 1:
        //         break;
        //     case 2:
                // if(ratesStopped){
                    setLoadingSaveRate(true)                    
                    source.cancel('Operation canceled by the user.')
                    setRatesStopped(true)
                    handleClose()

                    axios.post('/applet/v1/rate/save', {
                        rateId: rowData?.id,
                        originShippingLocationId: formikValues?.shipper.id ? formikValues?.shipper.id : 0,
                        destinationShippingLocationId: formikValues?.consignee.id ? formikValues?.consignee.id : 0
                    }).then(results => {
                        setFormikValues(`quoteNumber`, results.data.data.results?.quoteNumber)
                        setFormikValues(`vendorId`, rowData?.vendorId)
                        setFormikValues(`vendorSCAC`, rowData?.SCAC)
                        const vendor = user.enabledVendors.find(vendor => vendor.id === rowData?.vendorId)
                        
                        if(vendor?.showBOLInstructions && vendor?.BOLInstructions && vendor?.BOLInstructions !== '<br>'){
                          setOpenAddRateRemarksModal(true)
                        }

                        if(vendor?.name && user?.allowVendorDisplay) {
                            setVendorName(vendor?.name);
                        }
                                            
                        setFormikValues(`carrierInformation`, {
                            name: rowData?.name || '',
                            total: parseFloat(rowData?.total) || '',
                            transitDays: rowData?.transitDays,
                            transitHours: rowData?.transitHours,
                            serviceLevel: rowData?.serviceLevel || '',
                            rateRemarks: rowData?.rateRemarks,
                            iconUrl: rowData?.iconUrl || '',
                            rateBreakdown: rowData?.rateBreakdown,
                            rateId: rowData?.id,
                            vendorId: rowData?.vendorId,
                            carrierCode: rowData?.carrierCode || '',
                            classesReClassed: rowData.freightInfoReclassed?.length ? rowData.freightInfoReclassed.map(f => f.class) : []                      
                        })
    
                        setLoadingSaveRate(false)                        
                    }).catch(error => {
                        setLoadingSaveRate(false)
                        checkErrorAPICall(error, dispatch, 'Save Rate')
                    })
                // }
                // break;
        // }
    }

    const carrierRender = (rowData) => {
        return (
          <>
            <img alt="icon" src={rowData.iconUrl} style={{width: 70}}/>
            <p style={{fontWeight: 500, marginBottom: 3}}>{rowData.name}</p>
            {user.showSCACScore ? <Rating value={rowData.rating} precision={0.5} readOnly /> : null}
          </>
        )
    }
         
    const serviceLevelRender = (rowData) => {
        return (
          <>
            <p className="mb-3" style={{fontWeight: 500}}>{rowData?.serviceLevel}</p>

            {rowData.rateRemarks?.length && rowData.rateRemarks[0]
                ? <div className='d-flex'>
                    <KTSVG path='/media/icons/bootstrap/exclamation-triangle-fill.svg' className='svg-icon svg-icon-6 text-danger me-1'/>
                    <div>
                        {rowData.rateRemarks.map(remark => {
                            return <><span key={remark} dangerouslySetInnerHTML={{ __html: remark }} /><br></br></>
                        })}
                    </div>
                </div>
                : null
            }
          </>
        )
    }

    // const actionsButtons = (rowData) => {
    //     return (    
    //     <Box sx={{display: 'flex', width: '100px'}}>
    //         <Tooltip sx={{width: '35px'}} title={!quote.filter(a => a.rateId === rowData.id)?.length ? 'Save Rate' : 'Download PDF'}>
    //         <span style={{width: '35px'}}>
    //           <Button 
    //             sx={{minWidth: '30px'}}
    //             onClick={() => {              
    //               setSelectedRow(rowData)
    //               if(!quote.filter(a => a.rateId === rowData.id)?.length){
    //                 setOpenSaveModal(true)
    //                 setSelectedRow(rowData)
    //               }else{
    //                 saveAs(quote.filter(a => a.rateId === selectedRow.id)[0]?.url)
    //               }
    //             }}
    //           >
    //             {!quote.filter(a => a.rateId === rowData.id)?.length ? 
    //             <SaveIcon style={{color: '#0095E8'}}/> : 
    //             <KTSVG className="svg-icon svg-icon-3 d-flex align-items-center justify-content-center" style={{color: '#0095E8'}} path='/media/icons/bootstrap/file-earmark-pdf-fill.svg'/>
    //             }
    //           </Button>
    //         </span>
    //         </Tooltip>
    //       {quote.filter(a => a.rateId === rowData.id)?.length > 0 && 
    //         <Tooltip sx={{width: '35px'}} title={quote.filter(a => a.rateId === rowData.id)?.length ? 'Email Documents' : ''}>
    //             <span style={{width: '35px'}}>
    //               <Button
    //                 sx={{minWidth: '30px'}}
    //                 onClick={() => {
    //                   setOpenEmailDocumentsModal(true)
    //                 }}
    //               >                
    //                 <EmailIcon style={{color: '#F1416C', width: 21, height: 21}}/>                
    //               </Button>
    //             </span>
    //         </Tooltip>
    //       }
    //       {user.allowBooking &&
    //         <Tooltip sx={{width: '35px'}} title={'Book Rate'}>
    //             <span style={{width: '35px'}}>
    //               <Button
    //                 sx={{minWidth: '30px'}}
    //                 onClick={() => {
    //                   setSelectedRow(rowData)
    //                   bookRate(rowData)
    //                 }}
    //               >
    //                 {(saving && rowData.id === selectedRow.id) ? <span className='spinner-border spinner-border-sm align-middle' style={{color: '#47BE7D'}}></span> : <BookIcon style={{color: '#47BE7D'}}/>}
    //               </Button>
    //             </span>
    //         </Tooltip>
    //       }
    //     </Box>      
    //     )
    // }
    
    const totalRender = (rowData) => {
        return (
            <button
                style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: 0,
                    textDecoration: 'underline'
                }}
                onClick={() => {
                    setQuoteBreakdownModalData(rowData)
                    setOpenQuoteBreakdownModal(true)
                }}
            >
                {dollarUSLocale.format(rowData.total)}
            </button>
        )
    }

    const serviceLevelCustomSort = (a, b) => (
        a.serviceLevel.trim()?.localeCompare(b.serviceLevel.trim())
    )

    const transitCustomSort = (a, b) => (
        (a.transitDays === "-" ? 99 : a.transitDays) - (b.transitDays === '-' ? 99 : b.transitDays)
    )

    // const [columns] = useState([
    //     { title: 'Carrier', field: 'name', render: carrierRender },
    //     { title: 'Service Level', field: 'serviceLevel', width: "50%", render: serviceLevelRender, customSort: serviceLevelCustomSort},
    //     { title: 'Est. Transit', field: 'transitDays', customSort: transitCustomSort },
    //     { title: 'Total', field: 'total', defaultSort: 'asc', type: 'currency',  render: totalRender}
    // ] as Column<Array<RowTableObject>>[])

    useEffect(() => {
        rates.forEach(rate => {
          if(!(rateTypes.filter(function(e) { return e.value === rate?.rateType; }).length > 0)){
            let stringLowerCase = rate.rateType?.toLowerCase()
            let stringFirstCharUpperCase = stringLowerCase?.charAt(0)?.toUpperCase() + stringLowerCase?.slice(1)
    
            setRateTypes([...rateTypes, {value: rate?.rateType, name: rate?.rateType?.length > 3 ? stringFirstCharUpperCase : rate?.rateType}])
          }
        })
    
        if(!(rates.length >= 1)){
          setRateTypeSelected('ALL RATES')
          setRateTypes([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rates])    

    useEffect(() => {
        let newRatesArray
        if(rateTypeSelected === 'ALL RATES'){
          newRatesArray = rates
        }else{
          newRatesArray = rates.filter(rate => {
            return rate.rateType === rateTypeSelected
          })
        }
    
        if(rateTypeSelected === 'ALL RATES'){
          setFilteredRates([])
        }
    
        setFilteredRates(newRatesArray)
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [rateTypeSelected, rates])

    const menuProps = {
        classes: {
            list: classesInput.selectPaperList
        }
    }

    return (
        <>
            <div className="col-3 d-flex align-items-center">
                <TextField
                    select
                    label=" "
                    value={rateTypeSelected}
                    onChange={(e) => {
                        setRateTypeSelected(e.target.value)
                    }}
                    className={clsx(classesInput.selectMUI)}
                    InputProps={{
                        style: stylesMUIInput(fontSize),
                    }}
                    SelectProps={{
                        IconComponent: KeyboardArrowDownIcon,
                        MenuProps: menuProps
                    }}
                    InputLabelProps={{shrink: false}}
                >
                    <MenuItem value='ALL RATES'>All Rates</MenuItem>
                    {
                        rateTypes.sort((a, b) => a.value?.localeCompare(b.value)).map((rateType, index) => (
                            <MenuItem key={index} value={rateType.value}>{rateType.name}</MenuItem>
                        ))
                    }
                </TextField>
            </div>
            <div className="col-6 d-flex justify-content-center align-items-center">
                {!loadingSaveRate ?
                    <Tooltip title={linearProgress >= 100 || ratesStopped ? "" : "Stop Get Rates"}>
                        <button
                            style={{
                                cursor: linearProgress >= 100 || ratesStopped ? 'auto' : 'pointer',
                                opacity: linearProgress >= 100 || ratesStopped ? '0' : '1',
                                transition: (linearProgress >= 100 && "opacity 0.5s ease-in") || (ratesStopped && "opacity 2.5s ease-in")
                            }}
                            className={`btn ${buttonColor} ${ratesStopped ? 'disabled' : ''} d-flex justify-content-center align-items-center`}
                            onClick={() => {
                                source.cancel('Operation canceled by the user.')
                                setRatesStopped(true)
                                setButtonColor('btn-success')
                                setTimeout(() => {
                                    setButtonColor('btn-danger')
                                }, 2550);
                            }}
                        >
                            {ratesStopped ? 'Stopped!' : <><ClearIcon fontSize="small" className="me-1"/>Stop</>}
                        </button>
                    </Tooltip>
                :  <div className='d-flex justify-content-center align-items-center'><CircularProgress style={{color: '#009EF7'}} size={30} /></div>}  
            </div>
            <div className="col-3">

            </div>

            <div className='d-flex justify-content-center mt-5'>
                <b>{linearProgress >= 100 || ratesStopped ? 'Rating finished' : 'Loading carriers...'}</b>
            </div>
            <div className='d-flex justify-content-center'>
                {`${linearProgress > 0 ? vendorsProcessed : '0'} of ${vendorsFiltered?.length} processed - ${rates?.length} ${rates?.length === 1 ? 'rate' : 'rates'} found`}
            </div>

            <div className="col-12 mt-3">
                <LinearProgress
                    variant="determinate"
                    value={linearProgress}
                    style={{backgroundColor: '#94d8ff'}}
                    className={classes.linearProgress}
                />
            </div>
            <div className="col-12">
                <div style={{maxHeight: 550, overflow: 'auto'}}>
                    {/* <MaterialTable
                        style={{boxShadow: 'none', fontFamily: 'Poppins !important'}}
                        columns={columns}
                        data={filteredRates.length >= 1 ? filteredRates : rates}
                        title=""
                        options={{
                            thirdSortClick: false,  
                            actionsColumnIndex: -1,
                            toolbar: false,
                            paging: false,
                            filtering: false,
                            searchFieldAlignment: 'left',
                            draggable: false,
                            rowStyle: (data, idx) => idx%2 !== 0 ? {
                                backgroundColor: '#f4f4f4',
                            } : {
                                backgroundColor: '#ffff',
                            },
                        }}   
                        components={{
                            Container: (props) => <Paper className={classes.table} {...props}/>,
                            Row: rowProps => <MTableBodyRowStyled {...rowProps} onRowClick={(e) => selectRate(e, rowProps.data)} onMouseEnter={(e) => e.target.parentElement.classList.add("hoverRow")} onMouseLeave={(e) => e.target.parentElement.classList.remove("hoverRow")} />,
                        }}
                    /> */}
                    
                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ width: '100%', mb: 2 }} className={classes.table}>        
                            <TableContainer>
                            <Table
                                sx={{ minWidth: 750}}
                                aria-labelledby="tableTitle"
                                size={'medium'}
                            >          
                                <EnhancedTableHead
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                // onSelectAllClick={handleSelectAllClick}
                                onRequestSort={handleRequestSort}
                                rowCount={visibleRows?.length}
                                />
                                <TableBody>            
                                {visibleRows?.map((row, index) => {
                                    const isItemSelected = selected.includes(row.id);
                                    const labelId = `enhanced-table-${index}`;

                                    return (
                                    <TableRow
                                        // hover
                                        onDoubleClick={(e) => selectRate(e, row)}
                                        // onClick={(e) => selectRate(e, row)} onMouseEnter={(e) => e.target.parentElement.classList.add("hoverRow")} onMouseLeave={(e) => e.target.parentElement.classList.remove("hoverRow")}
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.id}
                                        selected={isItemSelected}
                                        sx={{ backgroundColor: index %2 !== 0 ? '#f4f4f4':'#ffff', verticalAlign: 'top' }}
                                    >
                                        
                                        <TableCell
                                        component="th"
                                        id={labelId}
                                        scope="row"
                                        padding="none"
                                        sx={{width: 'calc(16.6667% + 0px)'}}
                                        >
                                        {carrierRender(row)}
                                        </TableCell>
                                        <TableCell align="left" style={{width: '50%'}}>{serviceLevelRender(row)}</TableCell>
                                        <TableCell align="center" style={{width: 'calc(16.6667% + 0px)'}}>{formatTransit(row, user?.enabledVendors)}</TableCell>
                                        <TableCell align="right" style={{width: 'calc(16.6667% + 0px)'}}>{totalRender(row)}</TableCell>
                                        {/* <TableCell align="right" style={{width: '144px'}}>{actionsButtons(row)}</TableCell> */}
                                    </TableRow>
                                    );
                                })}

                                {!rates.length && (
                                    <TableRow>                
                                    <TableCell align="center" colSpan={11}>
                                        <Box sx={{ paddingTop: '20px !important', paddingBottom: '20px !important' }}>No records to display</Box>
                                    </TableCell>                
                                    </TableRow>
                                )}
                                </TableBody>
                            </Table>
                            </TableContainer>
                        
                        </Paper>      
                    </Box>
                </div>
            </div>
            <QuoteBreakdownModal
                open={openQuoteBreakdownModal}
                setOpen={setOpenQuoteBreakdownModal}
                data={quoteBreakdownModalData}
            />
        </>
    )
})

export default RatesTable